// Form fields shared by the two alarm authoring surfaces: the recipe form
// (`AlarmDefinitionForm`) and the custom builder (phase 5). Severity and the
// watch schedule are the same question on both screens, and answering it two
// different ways — different copy, different markup, different validation
// message — is how the surfaces drift.
//
// These are presentational and uncontrolled by any draft type: each takes a
// value and a callback, so the recipe form keeps its `AlarmDraft` reducer and
// the custom builder keeps its own, with no shared state shape between them.
// Every non-trivial decision (which preset a stored mode set reads as, which
// modes a preset writes) lives in `lib/watchSchedule.ts` and is unit-tested
// there; these components only render.
//
// The default ids and test ids are the recipe form's EXISTING ones
// (`alarm-form-severity`, `alarm-form-mode-<mode>`), so extracting this changed
// no markup and no Playwright selector. The custom builder passes its own.

import type { VesselType } from '@neomaris/protocol';
import { Fragment, useState } from 'react';

import {
  ALARM_SEVERITIES,
  alarmSeverityLabel,
  severityDescription,
  type AlarmSeverity,
} from '../../lib/alarmDisplay';
import {
  CUSTOM_WATCH_HINT,
  CUSTOM_WATCH_LABEL,
  detectWatchPreset,
  watchGapNote,
  watchGridModes,
  watchPresetModes,
  watchPresets,
  type WatchPresetId,
} from '../../lib/watchSchedule';

interface SeveritySelectProps {
  value: AlarmSeverity;
  onChange: (severity: AlarmSeverity) => void;
  /** DOM id, also the `htmlFor` of the label. */
  id?: string;
  testId?: string;
  label?: string;
  disabled?: boolean;
}

/** The severity picker, rendered from the shared severity vocabulary. */
export function SeveritySelect({
  value,
  onChange,
  id = 'alarm-form-severity',
  testId = 'alarm-form-severity',
  label = 'Severity',
  disabled = false,
}: SeveritySelectProps) {
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className="form-input"
        value={value}
        disabled={disabled}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value as AlarmSeverity)}
      >
        {ALARM_SEVERITIES.map((sev) => (
          <option key={sev} value={sev}>
            {alarmSeverityLabel(sev)}
          </option>
        ))}
      </select>
      {/* What picking this one actually DOES. Four words in a dropdown cannot
          carry the difference between "Alarm" and "Warning"; the line under it
          can, and it is the only place the severity's own behaviour is stated
          before the alarm is saved. */}
      <p className="alarm-form-desc" data-testid={`${testId}-description`}>
        {severityDescription(value)}
      </p>
    </div>
  );
}

interface ModeCheckboxGridProps {
  /** Every selectable mode, in display order. */
  modeUniverse: readonly string[];
  /** The checked subset. */
  enabledModes: readonly string[];
  onToggle: (mode: string) => void;
  /** Test id prefix; each checkbox is `${testIdPrefix}${mode}`. */
  testIdPrefix: string;
}

/**
 * The raw per-mode checkbox grid. Only rendered behind "Custom…" — the modes
 * are the monitor's internal vocabulary, and asking an owner to compose their
 * watch schedule out of them is the thing the presets exist to stop.
 *
 * The offered set is {@link watchGridModes} of the universe, which drops
 * `Sailing`: the compiler folds it onto `Underway` (core ADR-109 / cloud ADR
 * 0175), so on an updated boat that checkbox cannot change what the alarm
 * watches. Hiding it is a DISPLAY decision only — the draft's `enabledModes` is
 * never rewritten here, so a stored set carrying `Sailing` keeps it through an
 * untouched save and still renders its chip verbatim on every read surface.
 *
 * The checked state comes from {@link ModeCheckboxGridProps.enabledModes}
 * verbatim rather than a canonicalized copy, so an alarm carrying an unusual
 * set shows what it actually has.
 */
function ModeCheckboxGrid({
  modeUniverse,
  enabledModes,
  onToggle,
  testIdPrefix,
}: ModeCheckboxGridProps) {
  return (
    <div className="alarm-form-modes">
      {watchGridModes(modeUniverse).map((m) => (
        <label key={m} className="alarm-form-mode">
          <input
            type="checkbox"
            checked={enabledModes.includes(m)}
            data-testid={`${testIdPrefix}${m}`}
            onChange={() => onToggle(m)}
          />
          <span>{m}</span>
        </label>
      ))}
    </div>
  );
}

interface WatchScheduleFieldProps {
  /** Every selectable mode, in display order (the canonical vocabulary). */
  modeUniverse: readonly string[];
  /** The draft's current mode set. */
  enabledModes: readonly string[];
  /** Apply a preset's whole mode set. */
  onSetModes: (modes: string[]) => void;
  /** Toggle one mode in the "Custom…" grid. */
  onToggleMode: (mode: string) => void;
  /** Validation message to show under the field, or undefined. */
  error?: string;
  /**
   * Radio-group name. Must be unique on the page — two groups sharing a name
   * would fight over which one is selected.
   */
  name: string;
  /** Test id prefix for the grid checkboxes; each is `${testIdPrefix}${mode}`. */
  testIdPrefix?: string;
  /** Test id prefix for the preset radios; each is `${presetTestIdPrefix}${id}`. */
  presetTestIdPrefix?: string;
  /**
   * What kind of boat this is (ADR 0173), or null/undefined when unknown. It
   * changes the WORDS only: a declared power boat reads "under way" instead of
   * "under power or sail" and is not shown the sailing gap note, because its
   * mode detector can never elect Sailing. The modes each preset WRITES are
   * identical on every boat — the Sailing ridealong stays.
   */
  vesselType?: VesselType | null;
}

/** "When should this alarm watch?" — the presets, with the grid behind Custom…. */
export function WatchScheduleField({
  modeUniverse,
  enabledModes,
  onSetModes,
  onToggleMode,
  error,
  name,
  testIdPrefix = 'alarm-form-mode-',
  presetTestIdPrefix = 'alarm-form-watch-',
  vesselType,
}: WatchScheduleFieldProps) {
  const presets = watchPresets(vesselType);
  // "Custom…" is BOTH a detected state (the stored modes match no preset) and a
  // chosen one (they do match, but the operator wants the grid anyway), so the
  // choice needs a bit of local state on top of the detection. It is seeded
  // from the value this field opened with, which is what makes an unusual
  // stored set open with its grid already showing.
  const detected = detectWatchPreset(enabledModes);
  const [customChosen, setCustomChosen] = useState<boolean>(() => detected === 'custom');
  const selected: WatchPresetId = customChosen ? 'custom' : detected;
  // What the selected sentence claims but this alarm's stored modes do not
  // cover — in practice an older def that reads "Always" without Sailing. The
  // radio cannot be made honest by rewriting the def (that would re-push the
  // boat's ruleset on sight), so the difference is SAID. Re-picking the preset
  // writes the full set and the note goes away.
  const gapNote = selected === 'custom' ? null : watchGapNote(enabledModes, vesselType);

  function choose(id: WatchPresetId): void {
    if (id === 'custom') {
      // Deliberately writes NOTHING. Revealing the grid must not change the
      // alarm — the operator has not answered yet, they have only asked to see
      // the question in full.
      setCustomChosen(true);
      return;
    }
    setCustomChosen(false);
    onSetModes(watchPresetModes(id));
  }

  return (
    <fieldset className="alarm-form-fieldset">
      <legend>When should this alarm watch?</legend>
      <div className="alarm-form-watch">
        {presets.map((preset) => (
          <Fragment key={preset.id}>
            <label className="alarm-form-watch-choice">
              <input
                type="radio"
                name={name}
                checked={selected === preset.id}
                data-testid={`${presetTestIdPrefix}${preset.id}`}
                onChange={() => choose(preset.id)}
                // Clicking the ALREADY-selected radio changes nothing, so no
                // change event fires — and the gap note above tells the operator
                // to do exactly that. Re-apply on the click instead, guarded so
                // an ordinary selection is not applied twice (React dispatches
                // onClick before onChange, and `selected` is still the previous
                // render's value here).
                onClick={() => {
                  if (selected === preset.id) choose(preset.id);
                }}
              />
              <span>
                {preset.label}
                <span className="alarm-form-hint"> ({preset.hint})</span>
              </span>
            </label>
            {gapNote !== null && selected === preset.id ? (
              <p
                className="alarm-form-hint alarm-form-watch-gap"
                data-testid={`${presetTestIdPrefix}gap`}
              >
                {gapNote}
              </p>
            ) : null}
          </Fragment>
        ))}
        <label className="alarm-form-watch-choice">
          <input
            type="radio"
            name={name}
            checked={selected === 'custom'}
            data-testid={`${presetTestIdPrefix}custom`}
            onChange={() => choose('custom')}
          />
          <span>
            {CUSTOM_WATCH_LABEL}
            <span className="alarm-form-hint"> ({CUSTOM_WATCH_HINT})</span>
          </span>
        </label>
      </div>
      {selected === 'custom' ? (
        <ModeCheckboxGrid
          modeUniverse={modeUniverse}
          enabledModes={enabledModes}
          onToggle={onToggleMode}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {error !== undefined ? <p className="alarm-form-err">{error}</p> : null}
    </fieldset>
  );
}
